import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material";

import useStyles from "./styles";
import { useGetAllCompanies } from "../../api/companies";
import { AttachUserRequest, useAttachUser } from "../../api/onboarding";

/**
 * Rattacher un compte a une societe.
 *
 * CE QUE CETTE BOITE REMPLACE
 * ---------------------------
 * Un acces SSH, un client MySQL et un INSERT ecrit a la main dans
 * `company_user` sur la base de production. C'est litteralement la reparation
 * qui a ete faite pour BA Conseil, transformee en deux champs et un bouton.
 *
 * L'ID utilisateur est fige : on arrive toujours ici depuis une ligne de la
 * liste, jamais a froid. Laisser le saisir ouvrirait la porte a un rattachement
 * sur le mauvais compte, sur une action qui n'a pas de bouton "annuler".
 */
type Props = {
  open: boolean;
  onClose: () => void;
  /** Compte concerne, issu de la ligne cliquee. */
  user: { user_id: number; email: string } | null;
  /** Societe pre-selectionnee quand la ligne en designe une (invitation). */
  defaultCompanyId?: number | null;
  defaultRole?: AttachUserRequest["role"];
};

export const AttachUserDialog: React.FC<Props> = ({
  open,
  onClose,
  user,
  defaultCompanyId,
  defaultRole,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { companies } = useGetAllCompanies();
  const [companyId, setCompanyId] = useState<number | null>(
    defaultCompanyId ?? null
  );
  const [role, setRole] = useState<AttachUserRequest["role"]>(
    defaultRole ?? "FLEET_MANAGER"
  );

  const { mutate: attach, isPending } = useAttachUser(() => {
    onClose();
  });

  /*
   * Seules les societes de flotte sont proposees.
   *
   * Rattacher un FLEET_MANAGER a une societe dont `is_fleet` vaut 0 produit un
   * membre que les Gates de l'API refusent ensuite systematiquement (voir
   * AuthServiceProvider, qui teste `company_user->company->is_fleet`). Le
   * rattachement "reussit", la personne reste dehors, et le diagnostic coute
   * une seconde demi-journee.
   */
  const fleetCompanies = useMemo(
    () =>
      (companies ?? []).filter(
        (c: any) => c?.is_fleet === 1 || c?.is_fleet === "1" || c?.is_fleet === true
      ),
    [companies]
  );

  // Reinitialise a chaque ouverture : sans ca, un rattachement precedent laisse
  // sa societe pre-selectionnee sur le compte suivant.
  React.useEffect(() => {
    if (open) {
      setCompanyId(defaultCompanyId ?? null);
      setRole(defaultRole ?? "FLEET_MANAGER");
    }
  }, [open, defaultCompanyId, defaultRole]);

  const selected =
    fleetCompanies.find((c: any) => Number(c.id) === Number(companyId)) ?? null;

  const handleSubmit = () => {
    if (!user || !companyId) return;
    attach({ user_id: user.user_id, company_id: Number(companyId), role });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm">
      <DialogTitle>
        {t("onboarding.attachTitle", "Rattacher à une société")}
      </DialogTitle>

      <DialogContent>
        <div className={classes.dialogBody}>
          <TextField
            label={t("onboarding.attachUser", "Compte")}
            value={user?.email ?? ""}
            disabled
            fullWidth
            size="small"
          />

          <Autocomplete
            options={fleetCompanies}
            value={selected}
            getOptionLabel={(option: any) => `${option.name} (#${option.id})`}
            isOptionEqualToValue={(option: any, value: any) =>
              Number(option.id) === Number(value?.id)
            }
            onChange={(_e, value: any) =>
              setCompanyId(value ? Number(value.id) : null)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={t("onboarding.attachCompany", "Société de flotte")}
              />
            )}
          />

          <TextField
            select
            size="small"
            fullWidth
            label={t("onboarding.attachRole", "Rôle")}
            value={role}
            onChange={(e) =>
              setRole(e.target.value as AttachUserRequest["role"])
            }
          >
            <MenuItem value="FLEET_MANAGER">
              {t("onboarding.roleFleetManager", "Gestionnaire de flotte")}
            </MenuItem>
            <MenuItem value="EMPLOYEE">
              {t("onboarding.roleEmployee", "Collaborateur")}
            </MenuItem>
          </TextField>

          {/*
            Ce que l'exploitant doit savoir AVANT de cliquer, pas apres.

            Sur un pré-compte d'invitation le mot de passe est le code
            d'invitation, que le propriétaire n'a jamais vu. Rattacher sans le
            dire donne l'impression d'avoir terminé, alors que la personne
            reste incapable de se connecter.
          */}
          <div className={classes.dialogNote}>
            {t(
              "onboarding.attachNote",
              "Le compte sera activé s'il ne l'était pas, et toute invitation en attente pour cette société sera clôturée. Le mot de passe n'est pas modifié : demandez à la personne de passer par « mot de passe oublié » si elle n'en a jamais défini."
            )}
          </div>
        </div>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>
          {t("common.cancel", "Annuler")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isPending || !companyId || !user}
        >
          {t("onboarding.attachConfirm", "Rattacher")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
